import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { priorityConfig } from '../tasks/taskConstants';

/**
 * Kompaktowy box "Zadania na dzis" - element gornego rzedu POS (StatsBar).
 * Kazdy kasjer widzi swoje BIEZACE zadania do zrobienia.
 * Zrodlo danych to samo co dashboard: GET /tasks?status=aktywne&user_login=...&location_id=...
 * (aktywne = 'nowe' + 'w_trakcie'). Odhaczenie -> POST /tasks/:id/toggle.
 */
const MAX_VISIBLE = 4;

const isOverdue = (task) => {
  if (!task.termin_wykonania) return false;
  if (task.status === 'zakonczone') return false;
  const [y, m, d] = String(task.termin_wykonania).substring(0, 10).split('-').map(Number);
  if (!y || !m || !d) return false;
  // new Date('2026-07-20') to polnoc UTC - zadanie na DZIS bylo przez to
  // "zalegle" od rana. Bez godziny termin uplywa z koncem dnia, lokalnie.
  const hhmm = String(task.godzina_wykonania || '').substring(0, 5);
  const deadline = hhmm
    ? new Date(y, m - 1, d, Number(hhmm.slice(0, 2)), Number(hhmm.slice(3, 5)), 0)
    : new Date(y, m - 1, d, 23, 59, 59);
  return deadline < new Date();
};

const formatDue = (task) => {
  if (!task.termin_wykonania) return '';
  const d = new Date(task.termin_wykonania);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const time = task.godzina_wykonania ? ` ${String(task.godzina_wykonania).substring(0, 5)}` : '';
  return `${dd}.${mm}${time}`;
};

// Lokalne "dzis" jako YYYY-MM-DD. NIE uzywac toISOString() - to UTC i po 22:00
// czasu polskiego wpisaloby zadanie na jutro.
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const PosTasksBox = ({ userLogin, locationId }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchTasks = useCallback(async () => {
    if (!userLogin) return;
    try {
      const params = new URLSearchParams();
      params.append('status', 'aktywne');
      params.append('user_login', userLogin);
      if (locationId) params.append('location_id', locationId);
      const res = await api.get(`/tasks?${params}`);
      if (!mountedRef.current) return;
      if (res?.data?.success) {
        const order = { pilny: 0, wysoki: 1, normalny: 2, niski: 3 };
        // Tylko zadania PRZYPISANE do mnie (nie ogolno-lokalizacyjne) - to sa "moje zadania".
        const mine = (res.data.data || []).filter((t) => t.przypisany_do === userLogin);
        mine.sort((a, b) => {
          const ov = (isOverdue(b) ? 1 : 0) - (isOverdue(a) ? 1 : 0);
          if (ov) return ov;
          return (order[a.priorytet] ?? 2) - (order[b.priorytet] ?? 2);
        });
        setTasks(mine);
      }
    } catch (e) {
      // ciche - box nie moze wywrocic POS
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [userLogin, locationId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchTasks();
    const iv = setInterval(fetchTasks, 60000);
    return () => { mountedRef.current = false; clearInterval(iv); };
  }, [fetchTasks]);

  const handleToggle = async (task) => {
    if (togglingIds.has(task.id)) return;
    setTogglingIds((prev) => new Set(prev).add(task.id));
    // Optymistycznie usun z listy (odhaczone znika z "aktywne").
    setTasks((prev) => prev.filter((t) => t.id !== task.id));
    try {
      await api.post(`/tasks/${task.id}/toggle`);
    } catch (e) {
      fetchTasks(); // przywroc realny stan przy bledzie
    } finally {
      setTogglingIds((prev) => { const n = new Set(prev); n.delete(task.id); return n; });
    }
  };

  const handleCreate = async () => {
    const tytul = newTitle.trim();
    if (!tytul || saving) return;
    setSaving(true);
    try {
      const res = await api.post('/tasks', {
        tytul,
        kategoria: 'dzis',
        priorytet: 'normalny',
        status: 'nowe',
        termin_wykonania: todayLocal(),
        // Bez przypisany_do zadanie nie przeszloby filtra "moje" ponizej i
        // zniknieloby kasjerowi z oczu zaraz po utworzeniu.
        przypisany_do: userLogin,
        utworzyl: userLogin,
        location_id: locationId || null,
      });
      if (res?.data?.success) {
        setNewTitle('');
        setAdding(false);
        fetchTasks();
      }
    } catch (e) {
      // zostaw wpisany tekst - kasjer moze poprawic i sprobowac ponownie
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const startAdding = () => {
    setAdding(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (!userLogin) return null;
  if (loading && tasks.length === 0) return null; // nie migaj pustym boxem przy starcie

  const overdueCount = tasks.filter(isOverdue).length;
  const accent = overdueCount > 0 ? 'var(--danger)' : (tasks.length > 0 ? 'var(--info)' : 'var(--success)');
  const visible = tasks.slice(0, MAX_VISIBLE);
  const extra = tasks.length - visible.length;

  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${accent}`,
        borderRadius: '4px',
        padding: '0.4rem 0.6rem',
        marginBottom: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        fontSize: '12px',
        minWidth: '190px',
        maxWidth: '230px',
        flex: '1 1 190px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Naglowek */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 700, color: 'var(--text)', minWidth: 0 }}>
          <span style={{ whiteSpace: 'nowrap' }}>📋 Zadania na dziś</span>
          <span
            style={{
              backgroundColor: accent,
              color: '#fff',
              borderRadius: '10px',
              padding: '0 6px',
              fontSize: '10px',
              fontWeight: 700,
              minWidth: '16px',
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {tasks.length}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
          <button
            onClick={startAdding}
            title="Utwórz zadanie na dziś"
            style={{
              border: 'none', cursor: 'pointer', padding: 0,
              backgroundColor: 'var(--danger)', color: '#fff',
              width: '20px', height: '20px', borderRadius: '50%',
              fontSize: '15px', fontWeight: 700, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }}
          >
            +
          </button>
          <button
            onClick={fetchTasks}
            title="Odśwież"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '11px', padding: '0 2px' }}
          >
            ↻
          </button>
        </div>
      </div>

      {/* Szybkie dodawanie */}
      {adding && (
        <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
          <input
            ref={inputRef}
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }
            }}
            placeholder="Co do zrobienia?"
            maxLength={200}
            disabled={saving}
            style={{
              flex: 1, minWidth: 0, fontSize: '11px', padding: '3px 5px',
              border: '1px solid var(--border)', borderRadius: '3px',
              backgroundColor: 'var(--input-bg, var(--card-bg))', color: 'var(--text)',
            }}
          />
          <button
            onClick={handleCreate}
            disabled={saving || !newTitle.trim()}
            title="Dodaj"
            style={{
              border: 'none', borderRadius: '3px', padding: '3px 7px',
              fontSize: '11px', fontWeight: 700, flexShrink: 0,
              cursor: saving || !newTitle.trim() ? 'default' : 'pointer',
              backgroundColor: newTitle.trim() ? 'var(--info)' : 'var(--border)',
              color: newTitle.trim() ? '#fff' : 'var(--text-muted)',
            }}
          >
            ✓
          </button>
        </div>
      )}
      {overdueCount > 0 && (
        <div style={{ color: 'var(--danger)', fontSize: '10px', fontWeight: 700, marginTop: '2px' }}>
          ⚠ {overdueCount} zaległe
        </div>
      )}

      {/* Lista */}
      {tasks.length === 0 ? (
        // Pusty box to najlepszy moment na zachete - kasjer i tak tu patrzy.
        // Calosc klikalna, zeby nie trzeba bylo celowac w maly plusik.
        adding ? null : (
          <button
            onClick={startAdding}
            style={{
              border: '1px dashed var(--border)', borderRadius: '3px',
              background: 'transparent', cursor: 'pointer', textAlign: 'left',
              padding: '5px 6px', marginTop: '4px', width: '100%',
              display: 'flex', flexDirection: 'column', gap: '1px',
            }}
          >
            <span style={{ color: 'var(--success)', fontSize: '11px', fontWeight: 600 }}>
              ✓ Brak zadań na dziś
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '10px', lineHeight: 1.35 }}>
              Kliknij, aby dopisać, co masz dziś zrobić — nic Ci nie umknie w biegu.
            </span>
          </button>
        )
      ) : (
        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
          {visible.map((task) => {
            const pr = priorityConfig[task.priorytet] || priorityConfig.normalny;
            const overdue = isOverdue(task);
            const due = formatDue(task);
            return (
              <div
                key={task.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '2px 3px', borderRadius: '3px',
                  backgroundColor: overdue ? 'var(--danger-light)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={false}
                  disabled={togglingIds.has(task.id)}
                  onChange={() => handleToggle(task)}
                  title="Oznacz jako zrobione"
                  style={{ margin: 0, cursor: 'pointer', flexShrink: 0 }}
                />
                <span
                  aria-hidden="true"
                  title={pr.label}
                  style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: pr.text, flexShrink: 0 }}
                />
                <span
                  onClick={() => navigate('/tasks')}
                  style={{
                    flex: 1, cursor: 'pointer', color: 'var(--text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontWeight: task.status === 'w_trakcie' ? 600 : 400,
                  }}
                  title={task.tytul}
                >
                  {task.status === 'w_trakcie' && '🔵 '}
                  {task.tytul}
                </span>
                {due && (
                  <span
                    style={{
                      fontSize: '10px', flexShrink: 0,
                      color: overdue ? 'var(--danger)' : 'var(--text-muted)',
                      fontWeight: overdue ? 700 : 400,
                    }}
                  >
                    {overdue ? '⏰ ' : ''}{due}
                  </span>
                )}
              </div>
            );
          })}
          {extra > 0 && (
            <button
              onClick={() => navigate('/tasks')}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--info)', fontSize: '11px', textAlign: 'left', padding: '2px 4px' }}
            >
              +{extra} więcej →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PosTasksBox;
